import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronDown, ChevronUp, ChevronRight, Circle, FileText, Linkedin, CheckSquare } from 'lucide-react';
import './ProgressTimeline.css';

interface TimelineStep {
  id: string;
  title: string;
  description: string;
  date: string;
  completed: boolean;
  status?: "completed" | "current" | "upcoming";
  details?: string;
  iconType?: "linkedin" | "note" | "task" | "default";
  postUrl?: string;
  imageUrl?: string;
  videoUrl?: string;
  metadata?: {
    duration?: string;
    participants?: string[];
    tags?: string[];
    notes?: string;
    dueDate?: string;
  };
}

interface ProgressTimelineProps {
  steps: TimelineStep[];
  className?: string;
  enableAccordion?: boolean;
}

// ============================================================================
// TIMELINE ITEM COMPONENT WITH SCROLL ANIMATIONS
// ============================================================================

const TimelineItem: React.FC<{
  step: TimelineStep;
  index: number;
  isLast: boolean;
  expandedItems: Set<string>;
  onToggleExpanded: (itemId: string) => void;
  enableAccordion: boolean;
}> = ({ step, index, isLast, expandedItems, onToggleExpanded, enableAccordion }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [lineProgress, setLineProgress] = useState(0);
  const itemRef = useRef<HTMLDivElement>(null);
  const isExpanded = expandedItems.has(step.id);
  const hasDetails = step.details || step.metadata;

  // Determine status based on completed field
  const getStatus = () => {
    if (step.status) return step.status;
    return step.completed ? "completed" : "upcoming";
  };

  const status = getStatus();

  // Status-based styling configuration
  const getStatusStyles = () => {
    // Special styling for tasks
    if (step.iconType === "task") {
      return {
        iconBg: "timeline-dot-task",
        cardBorder: status === "completed" ? "timeline-content-completed" : "timeline-content-current",
        line: "timeline-line-task",
        dotClass: "timeline-dot-task"
      };
    }
    
    switch (status) {
      case "completed":
        return {
          iconBg: "timeline-dot-completed",
          cardBorder: "timeline-content-completed",
          line: "timeline-line-completed",
          dotClass: "timeline-dot-completed"
        };
      case "current":
        return {
          iconBg: "timeline-dot-current",
          cardBorder: "timeline-content-current",
          line: "timeline-line-current",
          dotClass: "timeline-dot-current"
        };
      case "upcoming":
        return {
          iconBg: "timeline-dot-pending",
          cardBorder: "timeline-content-pending",
          line: "timeline-line-pending",
          dotClass: "timeline-dot-pending"
        };
    }
  };

  const styles = getStatusStyles();

  // Icon based on iconType and status
  const getIcon = () => {
    // Use specific icons based on type
    if (step.iconType === "linkedin") {
      return <Linkedin className="timeline-icon" />;
    } else if (step.iconType === "note") {
      return <FileText className="timeline-icon" />;
    } else if (step.iconType === "task") {
      return <CheckSquare className="timeline-icon" />;
    }
    
    // Default icons based on status
    if (status === "completed") {
      return <Check className="timeline-icon" />;
    }
    
    switch (status) {
      case "current":
        return <ChevronRight className="timeline-icon" />;
      case "upcoming":
        return <Circle className="timeline-icon" />;
      default:
        return <Check className="timeline-icon" />;
    }
  };

  // Scroll-based animations
  useEffect(() => {
    const handleScroll = () => {
      if (!itemRef.current) return;

      const rect = itemRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const itemTop = rect.top;
      const itemHeight = rect.height;

      // Check if item is visible (trigger when 70% up the viewport)
      if (itemTop < windowHeight * 0.7) {
        setIsVisible(true);
      }

      // Calculate line progress based on scroll position
      if (!isLast) {
        const scrollProgress = Math.max(0, Math.min(100, 
          ((windowHeight * 0.5 - itemTop) / itemHeight) * 100
        ));
        setLineProgress(scrollProgress);
      }
    };

    handleScroll(); // Initial check
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLast]);

  return (
    <div 
      ref={itemRef} 
      className={`timeline-step ${status} ${isVisible ? 'timeline-visible' : 'timeline-hidden'}`}
    >
      {/* Date column */}
      <div className="timeline-step-left">
        <div className={`timeline-date ${isVisible ? 'timeline-date-visible' : 'timeline-date-hidden'}`}>
          {step.date}
        </div>
      </div>

      {/* Timeline line and icon */}
      <div className="timeline-step-center">
        <div className={`timeline-dot ${styles.dotClass} ${isVisible ? 'timeline-dot-visible' : 'timeline-dot-hidden'}`}>
          {getIcon()}
        </div>
        {!isLast && (
          <div className={`timeline-line ${styles.line}`}>
            <div
              className="timeline-line-progress"
              style={{ height: `${lineProgress}%` }}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="timeline-step-right">
        <div className={`timeline-content ${styles.cardBorder} ${isVisible ? 'timeline-content-visible' : 'timeline-content-hidden'}`}>
          {enableAccordion && hasDetails ? (
            <div 
              className="timeline-accordion-trigger"
              onClick={() => onToggleExpanded(step.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="timeline-header-content">
                <div className="timeline-header-row">
                  <div>
                    <h3 className="timeline-title">{step.title}</h3>
                    {step.description && <p className="timeline-description">{step.description}</p>}
                  </div>
                  <div className="timeline-chevron">
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
              </div>
              
              <div className={`timeline-accordion-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <div className="timeline-details">
                  <div className="timeline-content-wrapper">
                    {step.details && (
                      <div className="timeline-detail-section">
                        <p className="timeline-detail-text">{step.details}</p>
                      </div>
                    )}
                    
                    {(step.imageUrl || step.videoUrl) && (
                      <div className="timeline-media">
                        {step.imageUrl && (
                          <img 
                            src={step.imageUrl} 
                            alt="Post content" 
                            className="timeline-image"
                          />
                        )}
                        {step.videoUrl && !step.imageUrl && (
                          <video 
                            src={step.videoUrl} 
                            controls 
                            className="timeline-video"
                          />
                        )}
                      </div>
                    )}
                  </div>
                  
                  {step.postUrl && (
                    <div className="timeline-post-link">
                      <a 
                        href={step.postUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="timeline-link-button"
                      >
                        View on LinkedIn →
                      </a>
                    </div>
                  )}
                  
                  {step.iconType === "task" && step.metadata?.dueDate && (
                    <div className="timeline-post-link">
                      <div className="timeline-due-date">
                        Due: {(() => {
                          // Parse as local date to avoid timezone issues
                          const [year, month, day] = step.metadata.dueDate.split('-').map(Number);
                          const localDate = new Date(year, month - 1, day);
                          return localDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <h3 className="timeline-title">{step.title}</h3>
              {step.description && <p className="timeline-description">{step.description}</p>}
              {step.iconType === "task" && step.metadata?.dueDate && (
                <div className="timeline-due-date-simple">
                  Due: {(() => {
                    // Parse as local date to avoid timezone issues
                    const [year, month, day] = step.metadata.dueDate.split('-').map(Number);
                    const localDate = new Date(year, month - 1, day);
                    return localDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    });
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN PROGRESS TIMELINE COMPONENT
// ============================================================================

const ProgressTimeline: React.FC<ProgressTimelineProps> = ({ 
  steps, 
  className = '', 
  enableAccordion = true 
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  return (
    <div className={`progress-timeline ${enableAccordion ? 'accordion-timeline' : ''} ${className}`}>
      {steps.map((step, index) => (
        <TimelineItem
          key={step.id}
          step={step}
          index={index}
          isLast={index === steps.length - 1}
          expandedItems={expandedItems}
          onToggleExpanded={toggleExpanded}
          enableAccordion={enableAccordion}
        />
      ))}
    </div>
  );
};

export default ProgressTimeline;
